import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Package, MapPin, Calendar, Truck, Weight, Clock, Eye, Send, DollarSign, CheckCircle, XCircle, Building2, Ship, Plane, MessageSquare, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';

const RFQDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [myCompany, setMyCompany] = useState(null);
  
  const [quoteForm, setQuoteForm] = useState({
    base_rate: '',
    fuel_surcharge: '',
    handling_charges: '',
    insurance: '',
    customs_clearance: '',
    other_charges: '',
    currency: 'USD',
    transit_time_days: '',
    validity_days: '30',
    terms: '',
    notes: ''
  });
  
  // Counter-offer state
  const [counterOfferQuote, setCounterOfferQuote] = useState(null);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [showCounterDialog, setShowCounterDialog] = useState(false);
  const [counterOffers, setCounterOffers] = useState({});

  useEffect(() => {
    fetchRFQDetails();
    fetchMyCompany();
  }, [id]);

  const fetchRFQDetails = async () => {
    try {
      const [rfqRes, quotesRes] = await Promise.all([
        api.get(`/rfqs/${id}`),
        api.get(`/quotes/rfq/${id}`).catch(() => ({ data: [] }))
      ]);
      setRfq(rfqRes.data);
      // Sort quotes by total price from low to high
      const sortedQuotes = quotesRes.data.sort((a, b) => a.pricing.total - b.pricing.total);
      setQuotes(sortedQuotes);
    } catch (error) {
      toast.error('Failed to load RFQ details');
      navigate('/rfqs');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCompany = async () => {
    try {
      const res = await api.get('/companies/me');
      setMyCompany(res.data);
    } catch (error) {
      console.error('Failed to fetch company');
    }
  };

  const calculateTotal = () => {
    const baseRate = parseFloat(quoteForm.base_rate) || 0;
    const fuelSurcharge = parseFloat(quoteForm.fuel_surcharge) || 0;
    const handlingCharges = parseFloat(quoteForm.handling_charges) || 0;
    const insurance = parseFloat(quoteForm.insurance) || 0;
    const customsClearance = parseFloat(quoteForm.customs_clearance) || 0;
    const otherCharges = parseFloat(quoteForm.other_charges) || 0;
    return baseRate + fuelSurcharge + handlingCharges + insurance + customsClearance + otherCharges;
  };

  const handleSubmitQuote = async () => {
    if (!quoteForm.base_rate) {
      toast.error('Please enter at least a base rate');
      return;
    }

    setSubmittingQuote(true);
    try {
      const payload = {
        rfq_id: id,
        pricing: {
          base_rate: parseFloat(quoteForm.base_rate) || 0,
          fuel_surcharge: parseFloat(quoteForm.fuel_surcharge) || 0,
          handling_charges: parseFloat(quoteForm.handling_charges) || 0,
          insurance: parseFloat(quoteForm.insurance) || 0,
          customs_clearance: parseFloat(quoteForm.customs_clearance) || 0,
          other_charges: parseFloat(quoteForm.other_charges) || 0,
          total: calculateTotal(),
          currency: quoteForm.currency
        },
        transit_time_days: quoteForm.transit_time_days ? parseInt(quoteForm.transit_time_days) : null,
        validity_days: parseInt(quoteForm.validity_days) || 30,
        terms: quoteForm.terms || null,
        notes: quoteForm.notes || null
      };

      await api.post('/quotes', payload);
      toast.success('Quote submitted successfully!');
      setShowQuoteDialog(false);
      fetchRFQDetails();
      setQuoteForm({
        base_rate: '',
        fuel_surcharge: '',
        handling_charges: '',
        insurance: '',
        customs_clearance: '',
        other_charges: '',
        currency: 'USD',
        transit_time_days: '',
        validity_days: '30',
        terms: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmittingQuote(false);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/accept`);
      toast.success('Quote accepted!');
      fetchRFQDetails();
    } catch (error) {
      toast.error('Failed to accept quote');
    }
  };

  const handleDeclineQuote = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/decline`);
      toast.success('Quote declined');
      fetchRFQDetails();
    } catch (error) {
      toast.error('Failed to decline quote');
    }
  };

  const handleCounterOffer = async () => {
    if (!counterPrice || parseFloat(counterPrice) <= 0) {
      toast.error('Please enter a valid counter price');
      return;
    }
    
    try {
      await api.post(`/quotes/${counterOfferQuote.id}/counter?counter_price=${counterPrice}&message=${encodeURIComponent(counterMessage)}`);
      toast.success('Counter-offer sent!');
      setShowCounterDialog(false);
      setCounterOfferQuote(null);
      setCounterPrice('');
      setCounterMessage('');
      fetchRFQDetails();
    } catch (error) {
      toast.error('Failed to send counter-offer');
    }
  };

  const fetchCounterOffers = async (quoteId) => {
    try {
      const res = await api.get(`/quotes/${quoteId}/counters`);
      setCounterOffers(prev => ({ ...prev, [quoteId]: res.data }));
    } catch (error) {
      console.error('Failed to fetch counter-offers');
    }
  };

  const openCounterDialog = (quote) => {
    setCounterOfferQuote(quote);
    setCounterPrice(quote.pricing.total.toString());
    setShowCounterDialog(true);
    fetchCounterOffers(quote.id);
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return <Plane className="h-5 w-5 text-cyan-500" />;
      case 'sea': return <Ship className="h-5 w-5 text-cyan-500" />;
      case 'land': return <Truck className="h-5 w-5 text-cyan-500" />;
      default: return <Package className="h-5 w-5 text-cyan-500" />;
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  if (!rfq) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 p-8 text-center">
          <p>RFQ not found</p>
          <Button onClick={() => navigate('/rfqs')} className="mt-4">Back to RFQs</Button>
        </div>
      </>
    );
  }

  const isOwner = myCompany && rfq.company_id === myCompany.id;
  const canSubmitQuote = rfq.status === 'open' && !isOwner && myCompany?.status === 'active';
  const hasAlreadyQuoted = quotes.some(q => q.company_id === myCompany?.id);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-5xl mx-auto">
          <Button
            onClick={() => navigate('/rfqs')}
            variant="outline"
            className="mb-6"
            data-testid="back-to-rfqs"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Marketplace
          </Button>

          {/* RFQ Header Card */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-ocean-900 to-cyan-800 text-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <Badge className={
                    rfq.status === 'open' ? 'bg-green-500 text-white mb-3' :
                    rfq.status === 'closed' ? 'bg-slate-500 text-white mb-3' :
                    rfq.status === 'awarded' ? 'bg-blue-500 text-white mb-3' :
                    'bg-orange-500 text-white mb-3'
                  }>
                    {rfq.status.toUpperCase()}
                  </Badge>
                  <h1 className="text-3xl font-outfit font-bold mb-2">{rfq.title}</h1>
                  <div className="flex items-center gap-2 text-cyan-100">
                    <Building2 className="h-4 w-4" />
                    <span>Posted by {rfq.company_name}</span>
                  </div>
                </div>
                {canSubmitQuote && !hasAlreadyQuoted && (
                  <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-white text-ocean-900 hover:bg-slate-100" data-testid="submit-quote-button">
                        <Send className="mr-2 h-4 w-4" />
                        Submit Quote
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Submit Quote for: {rfq.title}</DialogTitle>
                        <DialogDescription>
                          {rfq.origin} → {rfq.destination} | {rfq.mode} | {rfq.cargo_type}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="base_rate">Base Rate *</Label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="base_rate"
                                type="number"
                                step="0.01"
                                value={quoteForm.base_rate}
                                onChange={(e) => setQuoteForm({ ...quoteForm, base_rate: e.target.value })}
                                className="pl-9"
                                placeholder="0.00"
                                data-testid="quote-base-rate"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="fuel_surcharge">Fuel Surcharge</Label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="fuel_surcharge"
                                type="number"
                                step="0.01"
                                value={quoteForm.fuel_surcharge}
                                onChange={(e) => setQuoteForm({ ...quoteForm, fuel_surcharge: e.target.value })}
                                className="pl-9"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="handling_charges">Handling Charges</Label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="handling_charges"
                                type="number"
                                step="0.01"
                                value={quoteForm.handling_charges}
                                onChange={(e) => setQuoteForm({ ...quoteForm, handling_charges: e.target.value })}
                                className="pl-9"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="customs_clearance">Customs Clearance</Label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="customs_clearance"
                                type="number"
                                step="0.01"
                                value={quoteForm.customs_clearance}
                                onChange={(e) => setQuoteForm({ ...quoteForm, customs_clearance: e.target.value })}
                                className="pl-9"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="insurance">Insurance</Label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="insurance"
                                type="number"
                                step="0.01"
                                value={quoteForm.insurance}
                                onChange={(e) => setQuoteForm({ ...quoteForm, insurance: e.target.value })}
                                className="pl-9"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="other_charges">Other Charges</Label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="other_charges"
                                type="number"
                                step="0.01"
                                value={quoteForm.other_charges}
                                onChange={(e) => setQuoteForm({ ...quoteForm, other_charges: e.target.value })}
                                className="pl-9"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-100 p-4 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">Total Quote Amount:</span>
                            <span className="text-2xl font-bold text-green-600">
                              ${calculateTotal().toFixed(2)} {quoteForm.currency}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="transit_time_days">Transit Time (Days)</Label>
                            <Input
                              id="transit_time_days"
                              type="number"
                              value={quoteForm.transit_time_days}
                              onChange={(e) => setQuoteForm({ ...quoteForm, transit_time_days: e.target.value })}
                              placeholder="e.g., 5"
                            />
                          </div>
                          <div>
                            <Label htmlFor="validity_days">Quote Valid For (Days)</Label>
                            <Input
                              id="validity_days"
                              type="number"
                              value={quoteForm.validity_days}
                              onChange={(e) => setQuoteForm({ ...quoteForm, validity_days: e.target.value })}
                              placeholder="30"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="terms">Terms & Conditions</Label>
                          <Textarea
                            id="terms"
                            value={quoteForm.terms}
                            onChange={(e) => setQuoteForm({ ...quoteForm, terms: e.target.value })}
                            placeholder="Payment terms, special conditions..."
                            rows={2}
                          />
                        </div>

                        <div>
                          <Label htmlFor="notes">Additional Notes</Label>
                          <Textarea
                            id="notes"
                            value={quoteForm.notes}
                            onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                            placeholder="Any additional information for the buyer..."
                            rows={2}
                          />
                        </div>

                        <div className="flex gap-3 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => setShowQuoteDialog(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSubmitQuote}
                            disabled={submittingQuote || !quoteForm.base_rate}
                            className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                            data-testid="confirm-submit-quote"
                          >
                            {submittingQuote ? 'Submitting...' : 'Submit Quote'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                {hasAlreadyQuoted && !isOwner && (
                  <Badge className="bg-green-500 text-white">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Quote Submitted
                  </Badge>
                )}
              </div>
            </div>

            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-cyan-500" />
                    <div>
                      <p className="text-sm text-slate-600">Route</p>
                      <p className="font-semibold text-lg">{rfq.origin} → {rfq.destination}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-cyan-500" />
                    <div>
                      <p className="text-sm text-slate-600">Cargo Type</p>
                      <p className="font-medium capitalize">{rfq.cargo_type?.replace('_', ' ')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {getModeIcon(rfq.mode)}
                    <div>
                      <p className="text-sm text-slate-600">Transport Mode</p>
                      <p className="font-medium capitalize">{rfq.mode}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {rfq.weight && (
                    <div className="flex items-center gap-3">
                      <Weight className="h-5 w-5 text-cyan-500" />
                      <div>
                        <p className="text-sm text-slate-600">Weight</p>
                        <p className="font-medium">{rfq.weight} {rfq.weight_unit}</p>
                      </div>
                    </div>
                  )}

                  {rfq.pieces && (
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-cyan-500" />
                      <div>
                        <p className="text-sm text-slate-600">Pieces</p>
                        <p className="font-medium">{rfq.pieces}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-cyan-500" />
                    <div>
                      <p className="text-sm text-slate-600">Posted</p>
                      <p className="font-medium">{new Date(rfq.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {rfq.closes_at && (
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-orange-500" />
                      <div>
                        <p className="text-sm text-slate-600">Closes</p>
                        <p className="font-medium">{new Date(rfq.closes_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {rfq.dimensions && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-slate-600 mb-1">Dimensions</p>
                  <p className="font-medium">{rfq.dimensions}</p>
                </div>
              )}

              {rfq.incoterms && (
                <div className="mt-4">
                  <p className="text-sm text-slate-600 mb-1">INCOTERMS</p>
                  <Badge variant="secondary">{rfq.incoterms}</Badge>
                </div>
              )}

              {rfq.special_handling && (
                <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-800 font-medium mb-1">Special Handling Requirements</p>
                  <p className="text-orange-900">{rfq.special_handling}</p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Views</p>
                    <p className="text-xl font-bold text-slate-700">{rfq.views_count}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-cyan-500" />
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Quotes Received</p>
                    <p className="text-xl font-bold text-cyan-500">{rfq.quotes_count}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quotes Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span>Quotes ({quotes.length})</span>
                  {quotes.length > 0 && (
                    <Badge variant="outline" className="text-xs font-normal">
                      Sorted: Lowest Price First
                    </Badge>
                  )}
                </div>
                {isOwner && rfq.status === 'open' && quotes.length > 0 && (
                  <Badge variant="secondary">Select a quote to award</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-center py-12 text-slate-600">
                  <Send className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg mb-2">No quotes received yet</p>
                  {!isOwner && canSubmitQuote && !hasAlreadyQuoted && (
                    <Button onClick={() => setShowQuoteDialog(true)} className="mt-4 bg-cyan-500 hover:bg-cyan-600">
                      Be the First to Quote
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote, index) => (
                    <div 
                      key={quote.id} 
                      className={`p-5 rounded-lg border-2 transition-all ${
                        quote.status === 'accepted' 
                          ? 'border-green-500 bg-green-50' 
                          : index === 0 && quote.status !== 'declined'
                          ? 'border-cyan-300 bg-cyan-50/30' 
                          : quote.status === 'declined'
                          ? 'border-red-200 bg-red-50'
                          : 'border-slate-200 bg-slate-50 hover:border-cyan-200'
                      }`}
                      data-testid={`quote-item-${quote.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="h-4 w-4 text-slate-500" />
                            <p className="font-semibold text-lg">{quote.company_name}</p>
                            {index === 0 && quote.status !== 'declined' && quote.status !== 'accepted' && (
                              <Badge className="bg-cyan-500 text-white text-xs">Lowest Price</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">
                            Submitted {new Date(quote.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={
                          quote.status === 'accepted' ? 'bg-green-500 text-white' :
                          quote.status === 'declined' ? 'bg-red-500 text-white' :
                          quote.status === 'viewed' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {quote.status.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-sm text-slate-600">Total Price</p>
                          <p className="text-2xl font-bold text-green-600">
                            {quote.pricing.currency} {quote.pricing.total.toFixed(2)}
                          </p>
                        </div>
                        {quote.transit_time_days && (
                          <div className="bg-white p-3 rounded-lg">
                            <p className="text-sm text-slate-600">Transit Time</p>
                            <p className="text-xl font-semibold">{quote.transit_time_days} days</p>
                          </div>
                        )}
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-sm text-slate-600">Valid Until</p>
                          <p className="font-medium">{new Date(quote.expires_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {/* Price Breakdown */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-4">
                        <div className="flex justify-between bg-white px-3 py-2 rounded">
                          <span className="text-slate-600">Base Rate:</span>
                          <span className="font-medium">${quote.pricing.base_rate.toFixed(2)}</span>
                        </div>
                        {quote.pricing.fuel_surcharge > 0 && (
                          <div className="flex justify-between bg-white px-3 py-2 rounded">
                            <span className="text-slate-600">Fuel:</span>
                            <span className="font-medium">${quote.pricing.fuel_surcharge.toFixed(2)}</span>
                          </div>
                        )}
                        {quote.pricing.handling_charges > 0 && (
                          <div className="flex justify-between bg-white px-3 py-2 rounded">
                            <span className="text-slate-600">Handling:</span>
                            <span className="font-medium">${quote.pricing.handling_charges.toFixed(2)}</span>
                          </div>
                        )}
                        {quote.pricing.customs_clearance > 0 && (
                          <div className="flex justify-between bg-white px-3 py-2 rounded">
                            <span className="text-slate-600">Customs:</span>
                            <span className="font-medium">${quote.pricing.customs_clearance.toFixed(2)}</span>
                          </div>
                        )}
                        {quote.pricing.insurance > 0 && (
                          <div className="flex justify-between bg-white px-3 py-2 rounded">
                            <span className="text-slate-600">Insurance:</span>
                            <span className="font-medium">${quote.pricing.insurance.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      {quote.notes && (
                        <p className="text-sm text-slate-700 p-3 bg-white rounded-lg mb-4">
                          <span className="font-medium">Notes: </span>{quote.notes}
                        </p>
                      )}

                      {quote.terms && (
                        <p className="text-sm text-slate-600 mb-4">
                          <span className="font-medium">Terms: </span>{quote.terms}
                        </p>
                      )}

                      {/* Action buttons for RFQ owner */}
                      {isOwner && rfq.status === 'open' && (quote.status === 'sent' || quote.status === 'counter') && (
                        <div className="flex gap-3 pt-3 border-t">
                          <Button 
                            onClick={() => handleAcceptQuote(quote.id)}
                            className="flex-1 bg-green-500 hover:bg-green-600"
                            data-testid={`accept-quote-${quote.id}`}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Accept Quote
                          </Button>
                          <Button 
                            onClick={() => openCounterDialog(quote)}
                            variant="outline"
                            className="flex-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                            data-testid={`counter-quote-${quote.id}`}
                          >
                            <ArrowRightLeft className="mr-2 h-4 w-4" />
                            Counter-Offer
                          </Button>
                          <Button 
                            onClick={() => handleDeclineQuote(quote.id)}
                            variant="outline"
                            className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`decline-quote-${quote.id}`}
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Decline
                          </Button>
                        </div>
                      )}
                      
                      {/* Show counter-offer status */}
                      {quote.status === 'counter' && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                            <ArrowRightLeft className="h-4 w-4" />
                            Counter-offer pending response
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Counter-Offer Dialog */}
          <Dialog open={showCounterDialog} onOpenChange={setShowCounterDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Counter-Offer</DialogTitle>
                <DialogDescription>
                  Negotiate a better price with {counterOfferQuote?.company_name}
                </DialogDescription>
              </DialogHeader>
              
              {counterOfferQuote && (
                <div className="space-y-4 pt-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Original Quote:</span>
                      <span className="font-semibold text-lg">
                        ${counterOfferQuote.pricing.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Your Counter Price *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="number"
                        step="0.01"
                        value={counterPrice}
                        onChange={(e) => setCounterPrice(e.target.value)}
                        className="pl-9"
                        placeholder="0.00"
                        data-testid="counter-price-input"
                      />
                    </div>
                    {counterPrice && parseFloat(counterPrice) < counterOfferQuote.pricing.total && (
                      <p className="text-sm text-green-600">
                        Saving: ${(counterOfferQuote.pricing.total - parseFloat(counterPrice)).toFixed(2)}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Message (optional)</Label>
                    <Textarea
                      value={counterMessage}
                      onChange={(e) => setCounterMessage(e.target.value)}
                      placeholder="Explain your counter-offer..."
                      rows={3}
                      data-testid="counter-message-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowCounterDialog(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCounterOffer}
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      data-testid="send-counter-offer-btn"
                    >
                      <ArrowRightLeft className="mr-2 h-4 w-4" />
                      Send Counter-Offer
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default RFQDetail;
